import { io } from 'socket.io-client';
import { firebaseAuth } from '../api/auth/auth.api';
import type { QueryClient } from '@tanstack/vue-query';
import type { SendImagePayload, SendMessagePayload } from '../api/chat/types/SendMessagePayload';
import type { StartChatPayload } from '../api/chat/types/StartChatPayload';
import type { Chat } from '../api/chat/types/Chat';
import { QueryKey } from '../api/common/types/query-key.enum';
import type { ChatMessage } from '../api/chat/types/ChatMessage';
import { toastController } from '@ionic/vue';
import { AlertTypeEnum, type Alert } from '@/api/alert/types/alert';
import type { User } from '../api/user/types/user';
import { alertEmitter } from '@/services/use-alert-events';
import environment from '@/environment';
import type { RouterInTabResult } from '@/hooks/use-router-in-tab';
import { RouteName } from '@/router';
import type { Group } from '../api/group/types/group';
import logger from './logger';
import { useAuthState } from '@/state/user';

const BASE_URL = environment.apiBaseUrl;
const msgAudio = new Audio('src/assets/sound/msg-sound.mp3');

export const socket = io(BASE_URL, {
  auth: async (cb: (data: object) => void) => {
    const currentUser = firebaseAuth.currentUser;
    let accessToken = '';
    if (currentUser) {
      const token = await currentUser.getIdToken();
      accessToken = token;
    }
    cb({ accessToken });
  },
  autoConnect: false,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SocketHandler = (...args: any[]) => void;

/**
 * The app-wide handlers registered by initSocket, kept by reference. initSocket runs
 * on startup and again on every native resume, so it must replace (not add to) the
 * previous set. Removal is by handler, so listeners anything else registers on the
 * shared socket are left alone.
 */
let appHandlers: Array<[event: string, handler: SocketHandler]> = [];

const detachAppHandlers = () => {
  for (const [event, handler] of appHandlers) socket.off(event, handler);
  appHandlers = [];
};

/**
 * Set whenever the socket goes down (native background or a dropped network). The
 * server does not queue chat messages for a disconnected client, and its chat push
 * has a per-chat cooldown, so a message sent meanwhile exists only on the server.
 * The next successful connection refetches the chat list once and clears it; the
 * first connection of the app never does.
 */
let chatsMissedWhileDisconnected = false;

export const initSocket = (queryClient: QueryClient, router: RouterInTabResult) => {
  const { isBackendUserProvisioned } = useAuthState();
  // The handshake looks up the GYMing user by uid and hard-disconnects when it
  // is missing, so connecting before provisioning is guaranteed to fail.
  if (!isBackendUserProvisioned.value) return;

  detachAppHandlers();
  const on = (event: string, handler: SocketHandler) => {
    socket.on(event, handler);
    appHandlers.push([event, handler]);
  };

  on('recieveMessage', async (message: ChatMessage) => {
    try {
      const isCurrentChat = router.currentRoute.value.path.endsWith(`${RouteName.Chat}/${message.chatId}`);
      if (isCurrentChat) {
        try {
          msgAudio.play();
        } catch {
          // Ignore audio errors - not critical
        }
        message.isRead = true;
      } else {
        try {
          const toast = await toastController.create({
            message: `${message.senderName} : ${message.content}`,
            duration: 1000 * 2,
            position: 'bottom',
            color: 'secondary',
            cssClass: 'alert-preview text-left text-base text-white max-h-[90vh] ',
          });

          await toast.present();
        } catch (toastError) {
          logger.error({
            message: 'Failed to show message toast',
            data: { chatId: message.chatId },
            error: toastError instanceof Error ? toastError : new Error(String(toastError)),
          });
        }
      }

      queryClient.setQueryData([QueryKey.GetChats], (oldData: Chat[]) => {
        if (!oldData) return [];
        const chatIndex = oldData.findIndex(chat => chat.id === message.chatId);
        if (chatIndex === -1) {
          queryClient.invalidateQueries({ queryKey: [QueryKey.GetChats] });
          return oldData;
        }
        const newChat = { ...oldData[chatIndex], messages: [...oldData[chatIndex].messages, message] };
        return [...oldData.slice(0, chatIndex), newChat, ...oldData.slice(chatIndex + 1)];
      });
    } catch (error) {
      logger.error({
        message: 'recieveMessage handler failed',
        data: { chatId: message.chatId },
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  });

  on('alert', async ({ alert, user }: { alert: Alert; user: User }) => {
    alertEmitter.emit('alert', { alert, user });
    if (alert.type === AlertTypeEnum.follow || alert.type === AlertTypeEnum.following) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.GetLoggedInUser] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.GetStories] }),
      ]);
    }
    if (alert.type === AlertTypeEnum.groupInvite) {
      queryClient.invalidateQueries({ queryKey: [QueryKey.GetGroups] });
    }
    queryClient.setQueryData([QueryKey.GetAlerts], (oldData: Alert[]) => {
      if (!oldData) return [];
      if (oldData.some(a => a.id === alert.id)) return oldData;
      return [...oldData, alert];
    });
  });

  on('removeAlert', async (alertId: string) => {
    queryClient.setQueryData([QueryKey.GetAlerts], (oldData: Alert[]) => {
      if (!oldData) return [];
      return oldData.filter(alert => alert.id !== alertId);
    });
  });

  // Handle group members update from backend
  on('groupUpdated', (updatedGroup: Group) => {
    queryClient.setQueryData<Group[]>([QueryKey.GetGroups], (oldGroups = []) => {
      return oldGroups.map(group => (group.id === updatedGroup.id ? updatedGroup : group));
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GetLeaderboard, 'group', updatedGroup.id],
    });
  });
  on('groupRemoved', (groupId: string) => {
    queryClient.setQueryData<Group[]>([QueryKey.GetGroups], (oldGroups = []) => {
      return oldGroups.filter(group => group.id !== groupId);
    });
  });

  on('disconnect', () => {
    chatsMissedWhileDisconnected = true;
  });

  on('connect', () => {
    if (!chatsMissedWhileDisconnected) return;
    chatsMissedWhileDisconnected = false;
    // Mounted chat views (the feed badge, the chat list, an open chat) refetch now;
    // unmounted ones are only marked stale and refetch when they mount, despite
    // useChats' staleTime. cancelRefetch: false joins a refetch already in flight,
    // such as App.vue's long-absence resume refresh, instead of sending a second one.
    void queryClient.invalidateQueries({ queryKey: [QueryKey.GetChats] }, { cancelRefetch: false });
  });

  // Handlers are in place before the first event can arrive; a no-op if already connected.
  socket.connect();
};

export const disconnectSocket = () => {
  detachAppHandlers();
  // The app's own 'disconnect' handler is gone by now, so record the gap here.
  if (socket.connected) chatsMissedWhileDisconnected = true;
  socket.disconnect();
};

export const startChat = (payload: StartChatPayload) => {
  socket.emit('startChat', payload);
};

export const sendMessage = (payload: SendMessagePayload | SendImagePayload) => {
  socket.emit('sendMessage', payload);
};
